import { useEffect, useState } from "react";
import { http } from "../../axios/axios";

import AdminHeader from "../../component/AdminHeader";
import StatCard from "../../component/StatCard";
import { Package } from "lucide-react";
import Pagination from "../../component/Pagination";
import AdminResolutionModal from "../../component/AdminResolutionModal";
import { toast } from "react-toastify";

export default function QueryAdmin() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [allQuery, setAllQuery] = useState([]);
  const [pagination, setPagination] = useState({});
  const limit = 40;
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);
  const [openMessageId, setOpenMessageId] = useState(null);
  const [count, setCount] = useState({
    pending: 0,
    resolved: 0,
  });
  const handleResolve = async (resolutionNote) => {
    if (!selectedQuery) return;

    try {
      setResolvingId(selectedQuery._id);
      const { data } = await http.patch(
        `queries/${selectedQuery._id}/resolve`,
        {
          resolutionNote,
        },
      );

      setAllQuery((current) =>
        current.map((query) =>
          query._id === data.data._id ? data.data : query,
        ),
      );
      setCount((current) => ({
        pending: Math.max(0, current.pending - 1),
        resolved: current.resolved + 1,
      }));
      setSelectedQuery(null);
      toast.success(data.message);
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          "Unable to send email. Request was not marked as resolved.",
      );
    } finally {
      setResolvingId(null);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this inquiry?",
    );

    if (!confirmDelete) return;

    try {
      await http.delete(`queries/${id}`);

      if (openMessageId === id) {
        setOpenMessageId(null);
      }

      await fetchQueries();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchQueries = async () => {
    try {
      const res = await http.get("queries", {
        params: {
          page,
          limit,
          status: status === "all" ? undefined : status,
        },
      });
      setAllQuery(res.data?.data);
      setPagination(res.data?.pagination);
      setCount(res.data?.count);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchQueries();
  }, [page, status]);
  return (
    <div className="p-4 md:p-8 min-h-screen text-slate-200">
      <AdminHeader title="Manage Product Inquiries" />

      <div className="mb-6 flex items-center justify-end gap-3">
        <label className="text-sm text-slate-400 ">Status</label>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border-slate-700 bg-slate-900 px-4 py-2 text-white outline-none focus:border-blue-500"
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>
      <div className="flex flex-col md:flex-row w-full mb-8 gap-4">
        <StatCard
          title={"Total Submissions"}
          icon={Package}
          count={pagination.total}
        />
        <StatCard
          title={"Total Pending"}
          icon={Package}
          count={count?.pending}
        />
        <StatCard
          title={"Total Resolved"}
          icon={Package}
          count={count?.resolved}
        />
      </div>

      <div className="space-y-4">
        {allQuery?.length === 0 ? (
          <div className="p-8 rounded bg-white/5 border border-white/10 text-center text-slate-400">
            No Inquiry Found
          </div>
        ) : (
          allQuery.map((query) => (
            <div
              key={query?._id}
              className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 hover:border-slate-700  hover:bg-slate-900/60 transition"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 space-y-1.5">
                  <h3 className="text-lg font-semibold text-white">
                    {query?.name}
                  </h3>

                  <p className="text-sm text-slate-400">{query?.phone}</p>
                  <p className="text-sm text-slate-400">{query?.email}</p>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {query.status === "pending" ? (
                      <button
                        onClick={() => setSelectedQuery(query)}
                        disabled={resolvingId === query._id}
                        className="rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-green-600"
                      >
                        {resolvingId === query._id
                          ? "Sending..."
                          : "Resolve & Send Email"}
                      </button>
                    ) : (
                      <div>
                        <span className="rounded-md bg-green-700 px-3 py-1.5 text-sm font-medium text-white">
                          ✓ Resolved
                        </span>
                        {query.resolutionEmailSent && (
                          <p className="mt-2 text-xs text-green-400">
                            Resolution email sent
                          </p>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => handleDelete(query?._id)}
                      className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <button
                  onClick={() =>
                    setOpenMessageId(
                      openMessageId === query?._id ? null : query?._id,
                    )
                  }
                  className="flex-shrink-0 p-2 text-slate-400 hover:text-white"
                >
                  <span
                    className={`inline-block transition-transform duration-300 ${
                      openMessageId === query?._id ? "rotate-180" : ""
                    }`}
                  >
                    ▼
                  </span>
                </button>
              </div>
              {openMessageId === query?._id && (
                <div className="mt-4 rounded-lg border border-slate-700 bg-slate-800 p-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {query?.companyName && (
                      <div>
                        <p className="text-xs text-slate-400">Company</p>
                        <p className="text-white">
                          {query?.companyName || "-"}
                        </p>
                      </div>
                    )}
                    {query?.city && (
                      <div>
                        <p className="text-xs text-slate-400">City</p>
                        <p className="text-white">{query?.city || "-"}</p>
                      </div>
                    )}

                    {query?.state && (
                      <div>
                        <p className="text-xs text-slate-400">State</p>
                        <p className="text-white">{query?.state || "-"}</p>
                      </div>
                    )}

                    {query?.propertyType && (
                      <div>
                        <p className="text-xs text-slate-400">Property Type</p>
                        <p className="text-white capitalize">
                          {query?.propertyType?.replaceAll("_", " ") || "-"}
                        </p>
                      </div>
                    )}

                    {query?.estimatedQuantity && (
                      <div>
                        <p className="text-xs text-slate-400">
                          Estimated Quantity
                        </p>
                        <p className="text-white">
                          {query?.estimatedQuantity || "-"}
                        </p>
                      </div>
                    )}

                    {query?.preferredBrand && (
                      <div>
                        <p className="text-xs text-slate-400">
                          Preferred Brand
                        </p>
                        <p className="text-white">
                          {query?.preferredBrand || "-"}
                        </p>
                      </div>
                    )}

                    {query?.budgetRange && (
                      <div>
                        <p className="text-xs text-slate-400">Budget</p>
                        <p className="text-white">
                          {query?.budgetRange || "-"}
                        </p>
                      </div>
                    )}

                    {query?.preferredContactMethod && (
                      <div>
                        <p className="text-xs text-slate-400">
                          Preferred Contact
                        </p>
                        <p className="text-white capitalize">
                          {query?.preferredContactMethod || "-"}
                        </p>
                      </div>
                    )}

                    {query?.productCategory && (
                      <div>
                        <p className="text-xs text-slate-400">
                          Product Category
                        </p>
                        <p className="text-white">
                          {query?.productCategory?.name || "-"}
                        </p>
                      </div>
                    )}

                    {query?.message && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-slate-400">Message</p>
                        <p className="text-white whitespace-pre-wrap">
                          {query?.message || "No message"}
                        </p>
                      </div>
                    )}
                    {query?.inquiryDetails && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-slate-400">Inquiry Details</p>
                        <p className="whitespace-pre-wrap text-white">
                          {query.inquiryDetails}
                        </p>
                      </div>
                    )}
                    {query.resolvedAt && (
                      <div className="sm:col-span-2 border-t border-slate-700 pt-3">
                        <p className="text-xs text-slate-400">
                          Resolved: {new Date(query.resolvedAt).toLocaleString()}
                        </p>
                        {query.resolutionEmailSentAt && (
                          <p className="mt-1 text-xs text-slate-400">
                            Email sent: {new Date(query.resolutionEmailSentAt).toLocaleString()}
                          </p>
                        )}
                        {query.resolutionNote && (
                          <>
                            <p className="mt-3 text-xs text-slate-400">Resolution Note</p>
                            <p className="mt-1 whitespace-pre-wrap text-white">
                              {query.resolutionNote}
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
        {allQuery.length > 0 && (
          <Pagination
            page={page}
            totalPages={pagination?.totalPage}
            onPrevious={() => setPage((prev) => prev - 1)}
            onNext={() => setPage((prev) => prev + 1)}
          />
        )}
      </div>
      {selectedQuery && (
        <AdminResolutionModal
          title="Resolve Product Inquiry"
          fields={[
            { label: "Customer Name", value: selectedQuery.name },
            { label: "Email", value: selectedQuery.email },
            { label: "Phone", value: selectedQuery.phone },
            { label: "Company Name", value: selectedQuery.companyName || "-" },
            { label: "City", value: selectedQuery.city },
            { label: "State", value: selectedQuery.state },
            { label: "Product Category", value: selectedQuery.productCategory?.name },
            { label: "Property Type", value: selectedQuery.propertyType },
            { label: "Estimated Quantity", value: selectedQuery.estimatedQuantity || "-" },
            { label: "Preferred Brand", value: selectedQuery.preferredBrand || "-" },
            { label: "Budget Range", value: selectedQuery.budgetRange || "-" },
            { label: "Preferred Contact Method", value: selectedQuery.preferredContactMethod },
            { label: "Inquiry Details", value: selectedQuery.inquiryDetails, fullWidth: true },
            { label: "Message", value: selectedQuery.message, fullWidth: true },
          ]}
          noteLabel="Inquiry Response / Resolution Note"
          notePlaceholder="Briefly describe the response or product solution provided."
          sending={resolvingId === selectedQuery._id}
          onClose={() => setSelectedQuery(null)}
          onConfirm={handleResolve}
        />
      )}
    </div>
  );
}
